<template>
  <div class="countries-nav" id="countries-nav">
    <header class="countries-nav__header">
      <ToggleLabel
        :toggleable="toggleable"
        :closed="!open"
        title="Country, region"
        :selected="selectedLabel"
        :callback="toggleCountryList"
      ></ToggleLabel>
    </header>

    <div class="countries-nav__search" v-if="(open === true)">
      <input type="text" id="countries-nav__search__input" v-model="searchstring" placeholder="Search">

      <transition name="quickfade">
        <ul
          class="countries-nav__selected"
          v-if="selectMultiple && (selectedCountries.length > 0)"
        >
          <li
            class="countries-nav__selected__item"
            v-for="(country, index) in selectedCountries"
            v-bind:class="'color--' + getCountryColor(country.id)"
            @click="toggleCountry(country.id, country.name, country.type)"
          >
            <SelectionMarker
              class="countries-nav__selected__item__selectionmarker"
              :color="getCountryColor(country.id)"
              :selected="true"
              type="check"
            ></SelectionMarker>
            {{ country.name }}
          </li>
          <li
            v-if="selectedCountries && selectedCountries.length === 1"
            class="countries-nav__selected__item countries-nav__selected__item--unclickable color--gray-light"
          >
            {{ this.$Strings('addMoreToCompare') }}
          </li>
        </ul>
      </transition>
    </div>

    <ul class="countries-nav__list regions" v-if="(open === true)">
      <li
        class="countries-nav__list__item regions__item"
        v-for="region in sortByName(visibleRegions)"
        :class="{ isActive : countryIsActive(region.id, region.name, 'region') }"
        v-if="rscIsVisible(region.id)"
        :key="region.id"
      >
        <SelectionMarker
          class="regions__item__selectionmarker"
          :color="'gray-darkest'"
          :selected="countryIsActive(region.id, region.name, 'region')"
          :type="selectMultiple ? 'check' : 'dot'"
        ></SelectionMarker>
        <CollapseLabel
          class="regions__item__name"
          :title="region.name"
          :closed="true"
          :canOpen="hasSubregions(region)"
          @toggleClick="toggleCountry(region.id, region.name, 'region')"
          @toggleOpen="toggleCountryRegion(region.id,'region')"
        ></CollapseLabel>

        <ul class="subregions" v-if="subregionExists(region.id)" v-show="countryRegionIsOpen(region.id, 'region')">
          <li
            class="subregions__item"
            v-for="subregion in sortByName(region.subregions)"
            :class="{ isActive : countryIsActive(subregion.id, subregion.name, 'subregion') }"
            :key="subregion.id"
            v-if="rscIsVisible(subregion.id)"
          >
          <SelectionMarker
            class="subregions__item__selectionmarker"
            :color="'gray-darkest'"
            :selected="countryIsActive(subregion.id, subregion.name, 'subregion')"
            :type="selectMultiple ? 'check' : 'dot'"
          ></SelectionMarker>
            <CollapseLabel
              class="subregions__item__name"
              :title="subregion.name"
              :closed="true"
              :canOpen="hasCountries(subregion)"
              @toggleClick="toggleCountry(subregion.id, subregion.name, 'subregion')"
              @toggleOpen="toggleCountryRegion(subregion.id, 'subregion')"
            ></CollapseLabel>

            <ul class="countries"
              v-for="country in sortByName(subregion.countries)"
              v-show="countryRegionIsOpen(subregion.id, 'subregion')"
              :key="country.id"
              v-if="rscIsVisible(country.id) && subregion.id != country.id"
            >
              <li class="countries__name"
                :class="{ isActive : countryIsActive(country.id, country.name, 'country') }"
                @click="toggleCountry(country.id, country.name, 'country')"
              >
                <SelectionMarker
                  class="countries__name__selectionmarker"
                  :color="'gray-darkest'"
                  :selected="countryIsActive(country.id, country.name, 'country')"
                  :type="selectMultiple ? 'check' : 'dot'"
                ></SelectionMarker>
                {{ country.name }}
              </li>
            </ul>
          </li>
        </ul>

        <div class="no-subregion"
          v-if="subregionExists(region.id) === false"
          v-for="subregion in sortByName(region.subregions)"
          :class="{ isActive : countryIsActive(subregion.id, subregion.name, 'subregion') }"
        >
          <ul class="countries"
            v-for="country in sortByName(subregion.countries)"
            v-show="countryRegionIsOpen(region.id, 'region')"
          >
            <li class="countries__name"
              :class="{ isActive : countryIsActive(country.id, country.name, 'country') }"
              @click="toggleCountry(country.id, country.name, 'country')"
            >
              <SelectionMarker
                class="countries__name__selectionmarker"
                :color="'gray-darkest'"
                :selected="countryIsActive(country.id, country.name, 'country')"
                :type="selectMultiple ? 'check' : 'dot'"
              ></SelectionMarker>
              {{ country.name }}
            </li>
          </ul>
        </div>

      </li>
    </ul>

    <div class="countries-nav__list__noresult" v-if="showNoSearchResultsText()">{{ this.$Strings('noResults') }}</div>

  </div><!-- /.compare-attributes-nav -->
</template>

<script>
import _ from 'lodash'
import ToggleLabel from '../shared/ToggleLabel'
import CollapseLabel from './CollapseLabel'
import SelectionMarker from './../shared/SelectionMarker'

import {EventBus} from '@/event-bus.js'

export default {
  name: 'compare-attributes-navigation',
  components: { SelectionMarker, ToggleLabel, CollapseLabel },
  props: {
    countries: {
      type: Object,
      required: true
    },
    closed: {
      type: Boolean,
      default: false
    },
    toggleable: {
      type: Boolean,
      default: false
    },
    closeOnSelect: {
      type: Boolean,
      default: false
    },
    selectMultiple: {
      type: Boolean,
      default: true
    }
  },
  data () {
    return {
      searchActive: false,
      open: false,
      searchstring: '',
      openCountryRegions: []
    }
  },
  computed: {
    selectedCountries () {
      return this.$store.state.rsc.selected
    },
    visibleRegions () {
      let result = {}
      let s = this.searchstring.toLowerCase()
      let countries = this.countries

      if (countries.regions === undefined) {
        return result
      } else {
        result = JSON.parse(JSON.stringify(countries.regions))

        // _.each(countries.regions, (r, rI) => {
        //   result[rI] = { id: r.id, name: r.name, type: 'region', subregions: {} }
        //   _.each(r.subregions, (sr, srI) => {
        //     result[rI].subregions[srI] = { id: sr.id, name: sr.name, type: 'subregion', countries: {} }
        //     _.each(sr.countries, (c, cI) => {
        //       result[rI].subregions[srI].countries[cI] = { id: c.id, name: c.name, type: 'country' }
        //     })
        //   })
        // })
      }

      // If no search just return entire result set
      if (s === '') {
        if (this.searchActive) {
          this.searchActive = false
          this.closeAllCountryRegion()
        }
        return result
      }

      this.searchActive = true

      _.each(result, (region, regionIndex) => {
        let keepRegion = false

        if (this.stringMatchesString(region.name, s)) {
          keepRegion = true
          this.openCountryRegion(region.id, 'region')
        }

        _.each(region.subregions, (subregion, subregionIndex) => {
          let keepSubregion = false

          if (this.stringMatchesString(subregion.name, s)) {
            keepRegion = true
            keepSubregion = true
            this.openCountryRegion(region.id, 'region')
            this.openCountryRegion(subregion.id, 'subregion')
          }

          _.each(subregion.countries, (country, countryIndex) => {
            if (this.stringMatchesString(country.name, s)) {
              keepRegion = true
              keepSubregion = true
              this.openCountryRegion(region.id, 'region')
              this.openCountryRegion(subregion.id, 'subregion')
            } else {
              delete result[regionIndex].subregions[subregionIndex].countries[countryIndex]
            }
          })

          if (!keepSubregion) {
            delete result[regionIndex].subregions[subregionIndex]
          }
        })

        if (!keepRegion) {
          delete result[regionIndex]
        }
      })

      return result
    },
    selectedLabel () {
      if (this.selectMultiple) { return false }
      let obj = _.first(this.selectedCountries)
      if (obj === undefined || obj.name === undefined) {
        return false
      }
      return obj.name
    }
  },
  mounted () {
    this.open = !this.closed

    let countries = this.countries
    if (countries.regions !== undefined) {
      let result = JSON.parse(JSON.stringify(countries.regions))
      let selected = this.selectedCountries

      _.each(result, (region, regionIndex) => {
        if (_.find(selected, [ 'id', region.id ])) {
          this.openCountryRegion(region.id, 'region')
        }

        _.each(region.subregions, (subregion, subregionIndex) => {
          if (_.find(selected, [ 'id', subregion.id ])) {
            this.openCountryRegion(subregion.id, 'subregion')
            this.openCountryRegion(region.id, 'region')
          }

          _.each(subregion.countries, (country, countryIndex) => {
            if (_.find(selected, [ 'id', country.id ])) {
              this.openCountryRegion(subregion.id, 'subregion')
              this.openCountryRegion(region.id, 'region')
            }
          })
        })
      })
    }
  },
  watch: {
    closed () {
      this.open = !this.closed
    },
    open () {
      this.$emit('toggleClosed', !this.open)
      // Set cursor in input if the countrylist is open
      if (this.open === true) {
        setTimeout(() => {
          let searchinput = document.getElementById('countries-nav__search__input')
          if (searchinput !== undefined && searchinput) {
            searchinput.focus()
          }
        }, 100)
      }
    }
  },
  methods: {
    rscIsVisible (rscId) {
      let onlyForCovid19Profile = this.$store.state.rsc.rscCovidProfileOnly.indexOf(parseInt(rscId))
      if (onlyForCovid19Profile === -1) {
        return true
      }

      return this.$router.history.current.path === '/indices/countries-regions-profile' &&
          this.$store.state.covid19.is_enabled
    },

    subregionExists (regionId) {
      if (this.$store.state.rsc.regionsWithoutSubregions.indexOf(parseInt(regionId)) !== -1) {
        return false
      }
      return true
    },

    hasSubregions (region) {
      return _.size(region.subregions) > 0
    },

    hasCountries (subregion) {
      return _.size(subregion.countries) > 0
    },

    /**
     * Sort the results alphabetically
     * @param  {Obj} regions Object with rsc data
     * @return {Obj}         The sorted object
     */
    sortByName (obj) {
      return _.sortBy(obj, ['name'])
    },

    /**
     * Check if there is any results
     * @return {Boolean}
     */
    showNoSearchResultsText () {
      return (this.open === true) && _.isEmpty(this.visibleRegions)
    },

    /**
     * Open or close a country or region
     * @param  {string} key The key/ID
     * @return {void}
     */
    toggleCountryRegion (key, type) {
      if (this.countryRegionIsOpen(key, type)) {
        this.closeCountryRegion(key, type)
      } else {
        this.openCountryRegion(key, type)
      }
    },

    /**
     * Open a country or region
     * @param  {string} key The key/ID
     * @return {void}
     */
    openCountryRegion (key, type) {
      if (!this.countryRegionIsOpen(key, type)) {
        this.openCountryRegions.push(key + '__' + type)
      }
    },

    /**
     * Close country or region
     * @param  {string} key The key/ID
     * @return {void}
     */
    closeCountryRegion (key, type) {
      if (this.countryRegionIsOpen(key, type)) {
        let index = this.openCountryRegions.indexOf(key + '__' + type)
        this.openCountryRegions.splice(index, 1)
      }
    },

    /**
     * Closes all region and subregions
     * @return {void}
     */
    closeAllCountryRegion () {
      if (this.countries.regions === undefined) return false

      _.each(this.countries.regions, (r, rId) => {
        this.closeCountryRegion(r.id, 'region')
        _.each(r.subregions, (sr, srId) => {
          this.closeCountryRegion(sr.id, 'subregion')
        })
      })
    },

    /**
     * Check if a country or region is open
     * @param  {string} key The key/ID
     * @return {boolean}
     */
    countryRegionIsOpen (key, type) {
      return (this.openCountryRegions.indexOf(key + '__' + type) !== -1)
    },

    /**
     * Checks if a country/region/subregion is currently selected
     * @param  {integer} id  The id
     * @param  {string} name The name
     * @param  {string} type The type
     * @return {boolean}
     */
    countryIsActive (id, name, type) {
      if (!id || !name || !type) {
        return false
      }

      let i = _.findIndex(this.$store.state.rsc.selected, (rsc) => {
        return rsc.id === id
      })
      let num = this.$store.state.rsc.selected.length

      if (this.selectMultiple || num <= 1) {
        return i !== -1
      } else {
        return i === 0
      }
    },

    stringMatchesString (str1, str2) {
      let str1lc = str1.toLowerCase()
      let str2lc = str2.toLowerCase()
      return (str1lc.search(str2lc) !== -1)
    },

    toggleCountry (id, name, type) {
      if (this.$store.state.rsc.notSelectableRegions.indexOf(id) !== -1) {
        this.toggleCountryRegion(id, type)
        return false
      }

      this.$store.commit('resetSelectionColor')

      if (!this.selectMultiple) {
        this.$store.commit('rsc/resetAllSelected')
      }

      // Limit selection to 7
      if (this.selectedCountries.length >= 7 && !this.countryIsActive(id, name, type)) {
        EventBus.$emit('popup.open', 'Maximum countries selected', 'The maximum amount of countries/regions are selected. Please unselect countries to compare others.')
        return false
      }
      this.$store.dispatch('rsc/toggleSelect', {
        id: id,
        name: name,
        type: type
      })
      // this.$store.commit('selections/toggleRsc', {
      //   route: this.$route.path,
      //   key: id
      // })

      if (this.closeOnSelect) { this.closeCountryList() }
    },
    toggleCountryList () {
      if (this.open === true) {
        this.closeCountryList()
      } else {
        this.openCountryList()
      }
    },
    openCountryList () {
      this.searchstring = ''
      this.open = true
    },
    closeCountryList () {
      this.searchstring = ''
      this.open = false
    },
    getCountryColor (key) {
      return this.$store.getters.getSelectionColor(key)
    }
  }
}
</script>

<style lang="scss" scoped>
@import '~@/components/sass/shared/colors';
@import '~@/components/sass/shared/fonts';
@import '~@/components/sass/shared/typography';

.countries-nav{
  border-bottom: 2px solid $gray-lighter;
  position: relative;
}

.countries-nav__search{
  border-bottom: 1px solid $gray-lighter;
  box-shadow: 0 5px 5px 0 transparentize($blue, 0.9);
  padding: 0 20px 20px;
  position: relative;
}
.countries-nav__search:before{
  background: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="16" heigth="16" viewBox="0 0 16.01 17.05"><g id="Lag_2" data-name="Lag 2"><g id="Layer_1" data-name="Layer 1"><circle fill="none" stroke="#bec4cc" stroke-miterlimit="10" cx="6.04" cy="6.04" r="5.54"/><line fill="none" stroke="#bec4cc" stroke-miterlimit="10" x1="9.39" y1="10.43" x2="15.65" y2="16.7"/></g></g></svg>');
  background-size: 16px 16px;
  content: "";
  display: block;
  left: 33px;
  height: 16px;
  position: absolute;
  top: 14px;
  width: 16px;
}

#countries-nav__search__input{
  background: transparent;
  border: 1px solid $gray-lighter;
  border-radius: 3px;
  font: #{$font-size}/1.4 $fontfamily-sans-serif;
  padding: 13px 13px 13px 40px;
  width: 100%;
}

#countries-nav__search__input:focus{
  outline: none;
  box-shadow: 0 0 5px 0 transparentize($blue, 0.5);
}

.countries-nav__selected,
.countries-nav__list{
  list-style: none;
  margin: 0;
  padding: 0;
}

.countries-nav__selected__item,
.countries-nav__list__item{
  cursor: pointer;
  padding: 1em 20px;
}
.countries-nav__selected__item--unclickable{ cursor: auto }

.countries-nav__selected{ margin: 1em 0 0; }
.countries-nav__selected__item{
  padding: 0.5em 20px;
  position: relative;

  .countries-nav__selected__item__selectionmarker{
    position: absolute;
    top: 12px;
    left: 0;
  }
}

.countries-nav__list__noresult{
  font-style: italic;
  padding: 1em 20px;
}

.regions,
.subregions,
.countries{
  list-style: none;
  margin: 0;
  padding: 0;
}

.countries{
  margin: 0 0 0 20px;
}

.countries__name{
  font-weight: 300;
  margin: 15px 0;
  position: relative;

  .countries__name__selectionmarker{
    position: absolute;
    top: 5px;
    left: -20px;
  }
}


.countries{ margin-left: 30px; }
.regions__item{
  border-bottom: 1px solid $gray-lighter;
  margin: 0;
  padding: 1em 20px;
  position: relative;

  .regions__item__selectionmarker{
    position: absolute;
    top: 27px;
    left: 7px;
  }
}
.regions__item:last-child{ border-bottom: none; }

.subregions__item{
  border-bottom: 1px solid $gray-lighter;
  font-weight: 600;
  margin: 0 -20px;
  padding: 16px 20px 16px 30px;
  position: relative;

  .subregions__item__selectionmarker{
    position: absolute;position: absolute;
    top: 21px;
    left: 12px;
  }
}
.subregions__item:last-child{ border-bottom: none; }

.regions__item__name{
  font-size: 15px;
  font-weight: bold;
  position: relative;
}
.subregions__item__name{
  font-weight: bold;
  position: relative;
}

.regions__item__name{ padding: 0.5em 0; }

.regions__item,
.subregions__item,
.countries__name{
  position: relative;
}

.regions__item.isActive:before{
  left: 8px;
  top: 21px;
}

.subregions__item.isActive:before{
  left: 15px;
}

.countries__name.isActive:before{
  left: -20px;
}

</style>



// WEBPACK FOOTER //
// src/components/shared/navigation/CountriesNavigation/Nav.vue